import { round } from 'es-toolkit';
import flow from 'lodash/flow';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { api } from 'src/client/api';
import { player } from 'src/client/player';
import { stores } from 'src/client/stores';
import { openOneTrustPreferenceCenter } from '../actions/app';
import { loginDesktop } from '../actions/auth';
import { showAvailableDesktopUpdateBanner } from '../actions/desktop';
import { closeNowPlayingDialog } from '../actions/dialog';
import { logClientError } from '../actions/logging';
import { desktopFuncNames } from '../constants/desktop';
import { DESKTOP_MANUAL_UPDATE_VERSIONS } from '../constants/experiments/desktop';
import { ACCOUNT_PATH, SEARCH_PATH } from '../constants/paths';
import { LocationAndLocalizationContext } from '../providers/LocationAndLocalizationProvider';
import {
  selectCcpaApplies,
  selectDesktopVersion,
  selectGdprApplies,
  selectPartnerId,
  selectTuneInUserSerial,
} from '../selectors/app';
import { selectExperiment, selectIsDesktopNowPlayingEnabled } from '../selectors/config';
import { selectIsNowPlayingDialogOpen } from '../selectors/dialog';
import invokeDesktopFunction from '../utils/desktop/invokeDesktopFunction';
import isDesktop from '../utils/desktop/isDesktop';
import registerDesktopHandler from '../utils/desktop/registerDesktopHandler';

// exported for testing
export function mapStateToProps(state) {
  const desktopVersion = selectDesktopVersion(state);
  const desktopManualUpdateVersions = selectExperiment(state, DESKTOP_MANUAL_UPDATE_VERSIONS);

  return {
    tuneInUserSerial: selectTuneInUserSerial(state),
    partnerId: selectPartnerId(state),
    shouldShowCcpaOptOut: selectCcpaApplies(state),
    isDesktopNowPlayingEnabled: selectIsDesktopNowPlayingEnabled(state),
    isNowPlayingDialogOpen: selectIsNowPlayingDialogOpen(state),
    shouldShowOneTrustGdprOptOut: selectGdprApplies(state) || false,
    showManualUpdateBanner: desktopManualUpdateVersions?.includes(desktopVersion) || false,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    desktopActions: bindActionCreators(
      {
        loginDesktop,
        logClientError,
        showAvailableDesktopUpdateBanner,
        openOneTrustPreferenceCenter,
        closeNowPlayingDialog,
      },
      dispatch,
    ),
  };
}

/**
 * withDesktopApp decorator implements initialization and updates for the
 * desktop handlers.
 *
 * On the client, we know that the environment is the desktop app because
 * the electron preload script adds a `Bridge` property on the window object
 * which allow us to expose handlers to desktop events
 *
 * The way we check the environment on the server is to pass through a
 * query param (`dsktp=true`) only when loading via electron.
 * This is used to identify the correct partnerId for web vs. desktop
 * and it's passed through here as an additional verification when
 * initializing the `isDesktop` function.
 */
export default function withDesktopApp(TargetComponent) {
  class ComponentWithDesktop extends Component {
    static propTypes = {
      desktopActions: PropTypes.object.isRequired,
      history: PropTypes.object.isRequired,
      partnerId: PropTypes.string,
      tuneInUserSerial: PropTypes.string,
      shouldShowCcpaOptOut: PropTypes.bool,
      shouldShowOneTrustGdprOptOut: PropTypes.bool.isRequired,
      isDesktopNowPlayingEnabled: PropTypes.bool.isRequired,
      isNowPlayingDialogOpen: PropTypes.bool.isRequired,
    };

    static contextType = LocationAndLocalizationContext;

    constructor(props, context) {
      super(props, context);

      // it's important to use the isDesktop util in this file (instead of app state);
      // we want the client side verification to guard window.Bridge functions
      if (isDesktop()) {
        // expose the APIS to be triggered by electron
        this.handleDesktopStore();
        this.setDesktopStaticHandlers();
        this.setPlayerVolumeHandlers();
        this.setPlayerStatusHandlers();

        // invoke event to allow desktop to access localizations
        invokeDesktopFunction(desktopFuncNames.setUpLocalizations);

        // invoke event to allow desktop to communicate by invoking logClientError
        // Important: invoke setUpGeminiEventSender after setUpLocalizations
        // to ensure localizations are set before they are utilized in gemini-desktop
        invokeDesktopFunction(desktopFuncNames.setUpGeminiEventSender);

        // invoke event to allow desktop app to display CCPA opt out page link in Help menu
        // Important: invoke showOptOutPageMenuItem after setUpGeminiEventSender,
        // as that's where the event listener for this call is added
        // Note: unable to change `showOptOutPageMenuItem` to `showCcpaOptOutPageMenuItem` without
        // breaking this functionality in older versions of gemini-desktop.
        if (props.shouldShowCcpaOptOut) {
          invokeDesktopFunction(desktopFuncNames.showOptOutPageMenuItem);
        } else {
          invokeDesktopFunction(desktopFuncNames.hideOptOutPageMenuItem);
        }

        // invoke event to allow desktop app to display GDPR settings link in Help menu
        // Important: invoke showOptOutPageMenuItem after setUpGeminiEventSender,
        // as that's where the event listener for this call is added
        if (props.shouldShowOneTrustGdprOptOut) {
          invokeDesktopFunction(desktopFuncNames.showGdprSettingsMenuItem);
        } else {
          invokeDesktopFunction(desktopFuncNames.hideGdprSettingsMenuItem);
        }

        if (props.showManualUpdateBanner) {
          props.desktopActions.showAvailableDesktopUpdateBanner(true);
        }

        api.updateReportingData({
          context: {
            mode: 'MODE_DESKTOP',
          },
        });
      }
    }

    componentDidUpdate(prevProps) {
      if (isDesktop()) {
        const { shouldShowOneTrustGdprOptOut } = this.props;

        if (!prevProps.shouldShowOneTrustGdprOptOut && shouldShowOneTrustGdprOptOut) {
          invokeDesktopFunction(desktopFuncNames.showGdprSettingsMenuItem);
        }
      }
    }

    /*
      Handlers that only need to be set once and
      do not require updates based on gemini-web data
    */
    setDesktopStaticHandlers() {
      const { history, desktopActions } = this.props;

      registerDesktopHandler(desktopFuncNames.handleDesktopHistoryFwd, () => {
        const { isDesktopNowPlayingEnabled, isNowPlayingDialogOpen } = this.props;
        if (isDesktopNowPlayingEnabled && isNowPlayingDialogOpen) {
          // Empty return here to ensure no confusing forwarding of history behind the NowPlayingDialog if open
          return;
        }
        history.goForward();
      });

      registerDesktopHandler(desktopFuncNames.handleDesktopHistoryBack, () => {
        const { isDesktopNowPlayingEnabled, isNowPlayingDialogOpen } = this.props;
        if (isDesktopNowPlayingEnabled && isNowPlayingDialogOpen) {
          this.props.desktopActions.closeNowPlayingDialog();
          return;
        }
        history.goBack();
      });

      registerDesktopHandler(desktopFuncNames.handleNavigateToAccount, () =>
        history.push(ACCOUNT_PATH),
      );

      registerDesktopHandler(desktopFuncNames.handleNavigateToOptOutPage, () =>
        desktopActions.openOneTrustPreferenceCenter(),
      );

      registerDesktopHandler(desktopFuncNames.handleOpenGdprSettings, () =>
        desktopActions.openOneTrustPreferenceCenter(),
      );

      registerDesktopHandler(desktopFuncNames.handleDesktopSearch, () => history.push(SEARCH_PATH));

      registerDesktopHandler(desktopFuncNames.logClientError, (logData) =>
        desktopActions.logClientError(logData),
      );

      registerDesktopHandler(desktopFuncNames.handleAvailableDesktopUpdate, () =>
        desktopActions.showAvailableDesktopUpdateBanner(),
      );

      registerDesktopHandler(desktopFuncNames.handleGoogleDesktopAuth, async (token) => {
        await desktopActions.loginDesktop(token);
        history.replace({
          ...window.location,
          // NOTE: Needed for the reloadOnPropsChange check in `client/Root.js`
          state: {
            authenticated: true,
          },
        });
      });

      registerDesktopHandler(desktopFuncNames.getLocalizedText, (name, values) =>
        this.context.getLocalizedText(name, values),
      );
    }

    setPlayerStatusHandlers() {
      invokeDesktopFunction(desktopFuncNames.enableMenuPlayPause);

      registerDesktopHandler(desktopFuncNames.handleDesktopPlayToggle, () => {
        player.onClick();
      });
    }

    setPlayerVolumeHandlers() {
      const volumeIncrement = 0.1;
      let showVolumeBarTimeoutId = null;

      const setDesktopVolume = (newVolume) => {
        const { setShowVolumeBar } = stores.playerStore.getState();

        player.setVolume(newVolume);

        setShowVolumeBar(true);

        if (showVolumeBarTimeoutId) {
          clearTimeout(showVolumeBarTimeoutId);
          showVolumeBarTimeoutId = null;
        }

        showVolumeBarTimeoutId = setTimeout(() => setShowVolumeBar(false), 1500);
      };

      registerDesktopHandler(desktopFuncNames.handleDesktopVolumeUp, () => {
        const volume = stores.playerStore.getState().volume;
        const incrementedVolume = round(volume + volumeIncrement, 2);
        const newVolume = incrementedVolume > 1 ? 1 : incrementedVolume;

        setDesktopVolume(newVolume);
      });

      registerDesktopHandler(desktopFuncNames.handleDesktopVolumeDown, () => {
        const volume = stores.playerStore.getState().volume;
        const decrementedVolume = round(volume - volumeIncrement, 2);
        const newVolume = decrementedVolume < 0 ? 0 : decrementedVolume;

        setDesktopVolume(newVolume);
      });
    }

    handleDesktopStore() {
      const { tuneInUserSerial, partnerId } = this.props;
      const serialFromDesktop = invokeDesktopFunction(desktopFuncNames.getSerial);
      // if there is no serial saved on the desktop
      if (!serialFromDesktop && tuneInUserSerial) {
        // set the rtid in the desktop store
        invokeDesktopFunction(desktopFuncNames.setSerial, tuneInUserSerial);
      }

      invokeDesktopFunction(desktopFuncNames.setPartnerId, partnerId);
    }

    render() {
      return <TargetComponent {...this.props} />;
    }
  }

  return flow(connect(mapStateToProps, mapDispatchToProps), withRouter)(ComponentWithDesktop);
}
